import React, { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { EtherealShadow } from "@/components/ui/ethereal-shadow";
import { ResponsiveImage } from "@/components/ResponsiveImage";
import LazyBackgroundImage from "@/components/ui/lazy-background-image";
import ContactConciergeModal from "@/components/ContactConciergeModal";
import EventContactModal from "@/components/EventContactModal";
import Footer from "@/components/Footer";
import AutoScrollGallery from "@/components/ui/auto-scroll-gallery";
import CalendlyEmbed from "@/components/CalendlyEmbed";
import CalendlyModal from "@/components/CalendlyModal";
import {
  Heart,
  Calendar,
  Users,
  Star,
  Camera,
  Music,
  Flower,
  Sparkles,
  Gift,
  PartyPopper,
  Building2,
  ImageIcon,
  Mic,
  Crown,
  Diamond,
  Palette,
  UtensilsCrossed,
  Wifi,
  Mountain,
  MapPin,
  Mail,
  ArrowRight,
} from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import heroEventBg from "@/assets/hero-event-bg.webp";
import heroEventBgPc from "@/assets/hero-event-bg-pc.webp";
import dreamWeddingBg from "@/assets/dream-wedding-bg.webp";
import privatePartiesBg from "@/assets/private-parties-bg.webp";
import { OptimizedImage } from "@/components/OptimizedImage";
import { getBlurPlaceholder } from "@/utils/blurPlaceholders";
import NewHeader from "@/components/NewHeader";
import SEO from "@/components/SEO";
import { getTranslatedStructuredData } from "@/data/structuredDataTranslations";
import { createBreadcrumbSchema } from "@/data/structuredData";
import { getPageSEO, SEOLanguage } from "@/data/seoTranslations";
import {
  trackEventConsultation,
  trackCalendlyEmbed,
} from "@/utils/eventTracking";

const Events: React.FC = () => {
  const { t, language } = useLanguage();
  const seoLang = language as SEOLanguage;
  const seoData = getPageSEO("events", seoLang);
  const breadcrumbSchema = createBreadcrumbSchema([
    { name: "Home", url: "https://beatenberg-resortspa.ch/" },
    { name: "Events", url: "https://beatenberg-resortspa.ch/events" },
  ]);
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [isEventContactModalOpen, setIsEventContactModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [isScrolling, setIsScrolling] = useState(false);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  const [isCalendlyOpen, setIsCalendlyOpen] = useState(false);
  const [currentHeroIndex, setCurrentHeroIndex] = useState(0);
  const scrollTimeoutRef = useRef<number | null>(null);

  const heroCards = [
    {
      id: "wedding" as const,
      title: t("events.dreamWeddings"),
      description: t("events.dreamWeddingsDescription"),
      ctaLabel: t("events.discussWeddingPackages"),
      image: "/images/Events/Dream Wedding.webp",
      alt: "Dream wedding celebration at Beatenberg Resort",
    },
    {
      id: "corporate" as const,
      title: t("events.corporateExcellence"),
      description: t("events.corporateExcellenceDescription"),
      ctaLabel: t("events.discussCorporatePackages"),
      image: "/images/Events/seminar-room-pc.webp",
      alt: "Corporate seminar room at Beatenberg Resort",
    },
    {
      id: "private_party" as const,
      title: t("events.privatePartiesTitle"),
      description: t("events.privatePartiesDescription"),
      ctaLabel: t("events.discussPrivatePartyPackages"),
      image: "/images/Events/Private parties.webp",
      alt: "Private party celebration at Beatenberg Resort",
    },
  ];

  // Detect prefers-reduced-motion
  useEffect(() => {
    const media = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setPrefersReducedMotion(media.matches);
    update();
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, []);

  // Auto-open Calendly when navigated with #consult hash
  useEffect(() => {
    if (window.location.hash === "#consult") {
      setIsCalendlyOpen(true);
      // Remove hash after opening to keep URL clean
      window.history.replaceState(
        null,
        "",
        window.location.pathname + window.location.search,
      );
    }
  }, []);

  // Listen for Calendly open event from EventContactModal
  useEffect(() => {
    const handleOpenCalendly = () => {
      setIsCalendlyOpen(true);
    };
    window.addEventListener("openCalendly", handleOpenCalendly);
    return () => window.removeEventListener("openCalendly", handleOpenCalendly);
  }, []);

  // Mobile hero carousel (match Sauna & Thermal Wellness behavior)
  useEffect(() => {
    setCurrentHeroIndex(0);
    const id = window.setInterval(() => {
      setCurrentHeroIndex((prev) => (prev + 1) % 3);
    }, 6000);
    return () => window.clearInterval(id);
  }, [language]);

  // Pause animation during active scroll
  useEffect(() => {
    const onScroll = () => {
      if (!isScrolling) setIsScrolling(true);
      if (scrollTimeoutRef.current)
        window.clearTimeout(scrollTimeoutRef.current);
      scrollTimeoutRef.current = window.setTimeout(
        () => setIsScrolling(false),
        200,
      );
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [isScrolling]);

  const shadowSpeed = prefersReducedMotion ? 0 : isScrolling ? 0 : 6;

  const services = [
    {
      icon: Heart,
      title: t("events.weddingPlanningService"),
      description: t("events.weddingPlanningDescription"),
      features: t("events.weddingPlanningFeatures").split(", "),
      details: t("events.weddingPlanningDetails"),
      highlights: t("events.weddingPlanningHighlights").split(", "),
    },
    {
      icon: Users,
      title: t("events.corporateEventsService"),
      description: t("events.corporateEventsServiceDescription"),
      features: t("events.corporateEventsServiceFeatures").split(", "),
      details: t("events.corporateEventsServiceDetails"),
      highlights: t("events.corporateEventsServiceHighlights").split(", "),
    },
    {
      icon: Sparkles,
      title: t("events.privateCelebrations"),
      description: t("events.privateCelebrationsDescription"),
      features: t("events.privateCelebrationsFeatures").split(", "),
      details: t("events.privateCelebrationsDetails"),
      highlights: t("events.privateCelebrationsHighlights").split(", "),
    },
  ];

  const testimonials = [
    {
      name: t("events.testimonial1Name"),
      event: t("events.testimonial1Event"),
      text: t("events.testimonial1Text"),
    },
    {
      name: t("events.testimonial2Name"),
      event: t("events.testimonial2Event"),
      text: t("events.testimonial2Text"),
    },
    {
      name: t("events.testimonial3Name"),
      event: t("events.testimonial3Event"),
      text: t("events.testimonial3Text"),
    },
  ];

  return (
    <>
      <SEO
        title={seoData.title}
        description={seoData.description}
        keywords={seoData.keywords}
        canonical="/events"
        language={seoLang}
        structuredData={[
          getTranslatedStructuredData.organization(seoLang),
          getTranslatedStructuredData.eventsVenue(seoLang),
          getTranslatedStructuredData.eventPlanningService(seoLang),
          getTranslatedStructuredData.eventsFAQ(seoLang),
          breadcrumbSchema,
        ]}
        data-oid="rysc.:1"
      />

      <div className="min-h-screen bg-background" data-oid="yfp10.q">
        <NewHeader data-oid="1h5v7zx" />
        {/* Hero Section - Single wrapper with interactive columns (like Sauna & Thermal Wellness) */}
        <section className="relative z-20" data-oid="events-hero-section">
          <div
            className="relative w-screen left-1/2 -translate-x-1/2 h-[80vh] lg:h-[750px] overflow-hidden group/events-hero"
            data-oid="events-hero-wrapper"
          >
            {/* MOBILE: Background Carousel */}
            <div className="lg:hidden absolute inset-0" data-oid="events-hero-mobile-carousel">
              {heroCards.map((card, index) => (
                <div
                  key={card.id}
                  className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${
                    index === currentHeroIndex ? "opacity-100 z-10" : "opacity-0 z-0"
                  }`}
                >
                  <OptimizedImage
                    src={typeof card.image === "string" ? card.image : heroEventBg}
                    alt={card.alt}
                    className="absolute inset-0 w-full h-full object-cover"
                    priority={index === 0}
                    fetchPriority={index === 0 ? "high" : "auto"}
                    blurDataURL={
                      typeof card.image === "string"
                        ? getBlurPlaceholder(card.image)
                        : undefined
                    }
                    sizes="100vw"
                    rootMargin="200px"
                  />
                  <div className="absolute inset-0 bg-black/45" />

                  <div className="absolute bottom-10 left-0 w-full text-center px-6 z-20">
                    <h3 className="text-white font-playfair text-3xl mb-3 drop-shadow-lg">
                      {card.title}
                    </h3>
                    <p className="text-white/85 text-sm font-lato max-w-sm mx-auto leading-relaxed mb-6">
                      {card.description}
                    </p>

                    <button
                      onClick={() => {
                        trackEventConsultation(
                          card.id,
                          card.ctaLabel,
                          "events_hero_mobile",
                          "calendly",
                        );
                        setIsCalendlyOpen(true);
                      }}
                      className="inline-flex items-center gap-3 text-white hover:text-alpine-gold transition-all duration-300"
                      data-oid={`events-hero-mobile-cta-${card.id}`}
                    >
                      <span className="font-lato text-[11px] uppercase tracking-[0.3em] font-semibold border-b border-white/30 pb-1">
                        {card.ctaLabel}
                      </span>
                      <div className="w-10 h-10 rounded-full bg-white/5 backdrop-blur-sm border border-white/20 flex items-center justify-center transition-all duration-300">
                        <ArrowRight className="w-4 h-4" />
                      </div>
                    </button>
                  </div>
                </div>
              ))}

              <div className="absolute bottom-5 left-1/2 -translate-x-1/2 flex gap-2 z-20">
                {heroCards.map((card, index) => (
                  <button
                    key={card.id}
                    type="button"
                    aria-label={`Show ${card.title}`}
                    onClick={() => setCurrentHeroIndex(index)}
                    className={`h-2 rounded-full transition-all duration-300 ${
                      index === currentHeroIndex ? "bg-alpine-gold w-7" : "bg-white/30 w-2"
                    }`}
                  />
                ))}
              </div>
            </div>

            {/* DESKTOP: 3 Interactive Columns */}
            <div className="hidden lg:flex h-full w-full" data-oid="events-hero-grid-desktop">
              {heroCards.map((card, index) => (
                <div
                  key={card.id}
                  className="relative group/hero-card h-full overflow-hidden transition-all duration-700 ease-in-out cursor-pointer border-r border-white/10 last:border-r-0 flex-[1] hover:flex-[2]"
                  data-oid={`events-hero-col-${card.id}`}
                >
                  <OptimizedImage
                    src={
                      typeof card.image === "string"
                        ? card.image
                        : index === 0
                          ? heroEventBgPc
                          : heroEventBg
                    }
                    alt={card.alt}
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-1000 group-hover/hero-card:scale-110"
                    priority={index === 0}
                    fetchPriority={index === 0 ? "high" : "auto"}
                    blurDataURL={
                      typeof card.image === "string"
                        ? getBlurPlaceholder(card.image)
                        : undefined
                    }
                    sizes="100vw"
                    rootMargin="200px"
                  />
                  <div className="absolute inset-0 bg-black/40 group-hover/hero-card:bg-black/20 transition-colors duration-500" />

                  <div className="absolute bottom-12 left-0 w-full text-center px-4 transform transition-transform duration-500 group-hover/hero-card:-translate-y-4 pointer-events-none">
                    <h2 className="text-white font-playfair text-2xl lg:text-3xl drop-shadow-lg">
                      {card.title}
                    </h2>

                    <div className="max-h-0 group-hover/hero-card:max-h-40 overflow-hidden opacity-0 group-hover/hero-card:opacity-100 transition-all duration-500 ease-in-out mt-4 px-6 pointer-events-auto">
                      <p className="text-white/90 text-sm font-lato leading-relaxed max-w-sm mx-auto mb-6">
                        {card.description}
                      </p>

                      <button
                        onClick={() => {
                          trackEventConsultation(
                            card.id,
                            card.ctaLabel,
                            "events_hero_desktop",
                            "calendly",
                          );
                          setIsCalendlyOpen(true);
                        }}
                        className="inline-flex items-center gap-3 text-white hover:text-alpine-gold transition-all duration-300 group/btn"
                        data-oid={`events-hero-desktop-cta-${card.id}`}
                      >
                        <span className="font-lato text-[11px] uppercase tracking-[0.3em] font-semibold border-b border-white/30 pb-1 group-hover/btn:border-alpine-gold">
                          {card.ctaLabel}
                        </span>
                        <div className="w-10 h-10 rounded-full bg-white/5 backdrop-blur-sm border border-white/20 group-hover/btn:border-alpine-gold flex items-center justify-center transition-all duration-300 transform group-hover/btn:translate-x-1">
                          <ArrowRight className="w-4 h-4" />
                        </div>
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Foreground Overlay (hero copy + quick CTA) */}
            <div className="absolute top-0 left-0 w-full z-20 pointer-events-none" data-oid="events-hero-overlay">
              <div className="w-full bg-gradient-to-b from-black/85 via-black/45 to-transparent pt-32 pb-10 px-6 md:px-12 pointer-events-auto">
                <div className="max-w-7xl mx-auto text-center text-white">
                  <h1 className="text-3xl md:text-5xl lg:text-6xl font-playfair font-semibold leading-tight drop-shadow-xl">
                    {t("events.heroTitle")}{" "}
                    <span className="bg-gradient-sunset bg-clip-text text-transparent">
                      {t("events.heroSubtitle")}
                    </span>
                  </h1>
                  <p className="mt-4 max-w-3xl mx-auto text-white/90 font-lato text-sm md:text-lg leading-relaxed drop-shadow-lg">
                    {t("events.heroDescription")}
                  </p>

                  <div className="mt-6 flex items-center justify-center gap-4">
                    <Button
                      onClick={() => {
                        trackEventConsultation(
                          "general",
                          t("events.requestQuote"),
                          "events_hero_overlay",
                          "contact_modal",
                        );
                        setIsEventContactModalOpen(true);
                      }}
                      variant="ghost-light"
                      size="lg"
                      className="border border-white/30 text-white hover:bg-white hover:text-black transition-all duration-500 rounded-full px-8"
                    >
                      {t("events.requestQuote")}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Smooth Transition - Gradient Fade */}
        <div className="relative w-full h-10 md:h-12 lg:h-16 z-30 pointer-events-none" data-oid="hero-transition-divider">
          <div
            className="absolute inset-0 bg-gradient-to-b from-transparent via-white/30 to-white"
            data-oid="gradient-overlay"
          />
        </div>

        {/* Dream Weddings Section - Full Screen - HIDDEN (now in hero) */}
        <section
          className="hidden relative h-screen flex items-center justify-center overflow-hidden z-30"
          data-oid="fxf3::_"
        >
          <LazyBackgroundImage
            src={dreamWeddingBg}
            alt="Dream wedding celebration at Beatenberg Resort"
            className="absolute inset-0"
            rootMargin="100px"
            data-oid="z31lvff"
          />

          <div
            className="relative z-10 max-w-7xl mx-auto px-6 w-full"
            data-oid="dnykcfk"
          >
            <div className="text-center mb-16 relative" data-oid="pk6hy3g">
              {/* Smooth shadow overlay behind text - oval shape to fit text layout */}
              <div
                className="absolute inset-0 flex items-center justify-center"
                data-oid="sol56an"
              >
                <div
                  className="w-[900px] h-[400px] rounded-full opacity-60"
                  style={{
                    background:
                      "radial-gradient(ellipse, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.4) 50%, transparent 70%)",
                  }}
                  data-oid="artjbkz"
                />
              </div>
              <div className="relative z-10" data-oid="5de:8n0">
                <div className="flex justify-center mb-6" data-oid="rcd-252">
                  <Heart
                    className="text-white text-8xl"
                    style={{
                      filter: "drop-shadow(2px 2px 8px rgba(0,0,0,0.8))",
                    }}
                    data-oid="1zaawq8"
                  />
                </div>
                <h2
                  className="text-5xl md:text-6xl font-playfair mb-6 text-white"
                  style={{
                    filter: "drop-shadow(2px 2px 8px rgba(0,0,0,0.8))",
                  }}
                  data-oid="gt7myfx"
                >
                  {t("events.dreamWeddings")}
                </h2>
                <p
                  className="text-xl text-white/95 max-w-3xl mx-auto"
                  style={{
                    filter: "drop-shadow(1px 1px 4px rgba(0,0,0,0.8))",
                  }}
                  data-oid="fwickwj"
                >
                  {t("events.dreamWeddingsDescription")}
                </p>
              </div>
            </div>

            <div
              className="flex flex-wrap gap-6 justify-center items-center w-full relative"
              data-oid="v6v79ky"
            >
              {/* Smooth shadow overlay for feature items - oval shape */}
              <div
                className="absolute inset-0 flex items-center justify-center"
                data-oid="556pdbf"
              >
                <div
                  className="w-[700px] h-[150px] rounded-full opacity-60"
                  style={{
                    background:
                      "radial-gradient(ellipse, rgba(0,0,0,0.5) 0%, rgba(0,0,0,0.2) 50%, transparent 70%)",
                  }}
                  data-oid="fnnz5w4"
                />
              </div>
              <div
                className="flex flex-col items-center text-center relative z-10"
                data-oid="3eqj-uh"
              >
                <Diamond
                  className="text-white mb-2"
                  size={24}
                  style={{ filter: "drop-shadow(1px 1px 4px rgba(0,0,0,0.8))" }}
                  data-oid="sqk-bz-"
                />

                <span
                  className="text-white text-sm md:text-base font-medium uppercase tracking-wider"
                  style={{ filter: "drop-shadow(1px 1px 4px rgba(0,0,0,0.8))" }}
                  data-oid="bwz_my0"
                >
                  {t("events.luxuryCeremonies")}
                </span>
              </div>
              <div
                className="w-[1.5px] h-8 bg-white/50 relative z-10 hidden md:block"
                style={{ filter: "drop-shadow(1px 1px 2px rgba(0,0,0,0.5))" }}
                data-oid="varev_2"
              ></div>
              <div
                className="flex flex-col items-center text-center relative z-10"
                data-oid="gyttj:p"
              >
                <Flower
                  className="text-white mb-2"
                  size={24}
                  style={{ filter: "drop-shadow(1px 1px 4px rgba(0,0,0,0.8))" }}
                  data-oid="nqa9-gk"
                />

                <span
                  className="text-white text-sm md:text-base font-medium uppercase tracking-wider"
                  style={{ filter: "drop-shadow(1px 1px 4px rgba(0,0,0,0.8))" }}
                  data-oid="tp4bqpt"
                >
                  {t("events.floralDesign")}
                </span>
              </div>
              <div
                className="w-[1.5px] h-8 bg-white/50 relative z-10 hidden md:block"
                style={{ filter: "drop-shadow(1px 1px 2px rgba(0,0,0,0.5))" }}
                data-oid="154q_-y"
              ></div>
              <div
                className="flex flex-col items-center text-center relative z-10"
                data-oid="d_8.aft"
              >
                <Palette
                  className="text-white mb-2"
                  size={24}
                  style={{ filter: "drop-shadow(1px 1px 4px rgba(0,0,0,0.8))" }}
                  data-oid="3d-563r"
                />

                <span
                  className="text-white text-sm md:text-base font-medium uppercase tracking-wider"
                  style={{ filter: "drop-shadow(1px 1px 4px rgba(0,0,0,0.8))" }}
                  data-oid="698x1tt"
                >
                  {t("events.themeDesign")}
                </span>
              </div>
              <div
                className="w-[1.5px] h-8 bg-white/50 relative z-10 hidden md:block"
                style={{ filter: "drop-shadow(1px 1px 2px rgba(0,0,0,0.5))" }}
                data-oid="7r.k4xu"
              ></div>
              <div
                className="flex flex-col items-center text-center relative z-10"
                data-oid="mpk21d2"
              >
                <Music
                  className="text-white mb-2"
                  size={24}
                  style={{ filter: "drop-shadow(1px 1px 4px rgba(0,0,0,0.8))" }}
                  data-oid="pntt155"
                />

                <span
                  className="text-white text-sm md:text-base font-medium uppercase tracking-wider"
                  style={{ filter: "drop-shadow(1px 1px 4px rgba(0,0,0,0.8))" }}
                  data-oid="4_wpima"
                >
                  {t("events.entertainment")}
                </span>
              </div>
            </div>
            <div className="text-center mt-12" data-oid="p4umiad">
              <Button
                onClick={() => {
                  trackEventConsultation(
                    "wedding",
                    t("events.discussWeddingPackages"),
                    "wedding_section",
                    "calendly",
                  );
                  setIsCalendlyOpen(true);
                }}
                variant="ghost-light"
                size="lg"
                className="border-gold text-gold hover:bg-gold hover:text-navy rounded-full shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 event-consultation-track"
                data-oid="qwtio9k"
              >
                {t("events.discussWeddingPackages")}
              </Button>
            </div>
          </div>
        </section>

        {/* Corporate Excellence Section - Full Screen - HIDDEN (now in hero) */}
        <section
          className="hidden relative h-screen flex items-center justify-center overflow-hidden z-30"
          data-oid="cd9y8in"
        >
          <ResponsiveImage
            src="/images/Events/seminar-room-pc.webp"
            alt="Corporate seminar room at Beatenberg Resort"
            className="absolute inset-0 w-full h-full object-cover"
            data-oid="dtrt_ze"
          />

          {/* Dark overlay for better text readability */}
          <div
            className="absolute inset-0 bg-black/40"
            data-oid="y3sc4-9"
          ></div>

          <div
            className="relative z-10 max-w-7xl mx-auto px-6 w-full"
            data-oid="s.hxh:k"
          >
            <div className="text-center mb-16 relative" data-oid="hj-0r_3">
              {/* Smooth shadow overlay behind text - oval shape to fit text layout */}
              <div
                className="absolute inset-0 flex items-center justify-center"
                data-oid="dmvvfm4"
              >
                <div
                  className="w-[900px] h-[400px] rounded-full opacity-60"
                  style={{
                    background:
                      "radial-gradient(ellipse, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.4) 50%, transparent 70%)",
                  }}
                  data-oid="ld9wwhr"
                />
              </div>
              <div className="relative z-10" data-oid="uzn4xdq">
                <div className="flex justify-center mb-6" data-oid="ps-:z.7">
                  <Building2
                    className="text-gold text-8xl"
                    style={{
                      filter: "drop-shadow(2px 2px 8px rgba(0,0,0,0.8))",
                    }}
                    data-oid="pdlqio7"
                  />
                </div>
                <h2
                  className="text-5xl md:text-6xl font-playfair mb-6 text-white"
                  style={{
                    filter: "drop-shadow(2px 2px 8px rgba(0,0,0,0.8))",
                  }}
                  data-oid="sqbxox2"
                >
                  {t("events.corporateExcellence")}
                </h2>
                <p
                  className="text-xl text-white/95 max-w-3xl mx-auto"
                  style={{
                    filter: "drop-shadow(1px 1px 4px rgba(0,0,0,0.8))",
                  }}
                  data-oid="kwbw:13"
                >
                  {t("events.corporateExcellenceDescription")}
                </p>
              </div>
            </div>

            <div
              className="flex flex-wrap gap-4 justify-center items-center w-full relative"
              data-oid="t:hztz3"
            >
              {/* Smooth shadow overlay for feature items - oval shape */}
              <div
                className="absolute inset-0 flex items-center justify-center"
                data-oid="_09inu3"
              >
                <div
                  className="w-[700px] h-[150px] rounded-full opacity-60"
                  style={{
                    background:
                      "radial-gradient(ellipse, rgba(0,0,0,0.5) 0%, rgba(0,0,0,0.2) 50%, transparent 70%)",
                  }}
                  data-oid="btrkis."
                />
              </div>
              <div
                className="flex flex-col items-center text-center relative z-10"
                data-oid="2pod_64"
              >
                <Users
                  className="text-white mb-2"
                  size={24}
                  style={{ filter: "drop-shadow(1px 1px 4px rgba(0,0,0,0.8))" }}
                  data-oid="3an6iv_"
                />

                <span
                  className="text-white text-sm md:text-base font-medium uppercase tracking-wider"
                  style={{ filter: "drop-shadow(1px 1px 4px rgba(0,0,0,0.8))" }}
                  data-oid="u4nsrf-"
                >
                  {t("events.conferences")}
                </span>
              </div>
              <div
                className="w-[1.5px] h-5 bg-white/50 relative z-10 hidden md:block"
                style={{ filter: "drop-shadow(1px 1px 2px rgba(0,0,0,0.5))" }}
                data-oid="deooxst"
              ></div>
              <div
                className="flex flex-col items-center text-center relative z-10"
                data-oid="2cc.:gd"
              >
                <Sparkles
                  className="text-white mb-2"
                  size={24}
                  style={{ filter: "drop-shadow(1px 1px 4px rgba(0,0,0,0.8))" }}
                  data-oid="t9ep81k"
                />

                <span
                  className="text-white text-sm md:text-base font-medium uppercase tracking-wider"
                  style={{ filter: "drop-shadow(1px 1px 4px rgba(0,0,0,0.8))" }}
                  data-oid="6m0iuf6"
                >
                  {t("events.productLaunches")}
                </span>
              </div>
              <div
                className="w-[1.5px] h-5 bg-white/50 relative z-10 hidden md:block"
                style={{ filter: "drop-shadow(1px 1px 2px rgba(0,0,0,0.5))" }}
                data-oid="7rbk:_k"
              ></div>
              <div
                className="flex flex-col items-center text-center relative z-10"
                data-oid="5w3q_cx"
              >
                <PartyPopper
                  className="text-white mb-2"
                  size={24}
                  style={{ filter: "drop-shadow(1px 1px 4px rgba(0,0,0,0.8))" }}
                  data-oid="7krsee8"
                />

                <span
                  className="text-white text-sm md:text-base font-medium uppercase tracking-wider"
                  style={{ filter: "drop-shadow(1px 1px 4px rgba(0,0,0,0.8))" }}
                  data-oid="a0zt6zz"
                >
                  {t("events.teamBuilding")}
                </span>
              </div>
              <div
                className="w-[1.5px] h-5 bg-white/50 relative z-10 hidden md:block"
                style={{ filter: "drop-shadow(1px 1px 2px rgba(0,0,0,0.5))" }}
                data-oid="ju_j_lf"
              ></div>
              <div
                className="flex flex-col items-center text-center relative z-10"
                data-oid="u7iczfq"
              >
                <Crown
                  className="text-white mb-2"
                  size={24}
                  style={{ filter: "drop-shadow(1px 1px 4px rgba(0,0,0,0.8))" }}
                  data-oid="bk842.y"
                />

                <span
                  className="text-white text-sm md:text-base font-medium uppercase tracking-wider"
                  style={{ filter: "drop-shadow(1px 1px 4px rgba(0,0,0,0.8))" }}
                  data-oid="oc0hoq9"
                >
                  {t("events.annualGalas")}
                </span>
              </div>
            </div>

            <div className="text-center mt-12" data-oid="ia4m0bp">
              <Button
                onClick={() => {
                  trackEventConsultation(
                    "corporate",
                    t("events.discussCorporatePackages"),
                    "corporate_section",
                    "calendly",
                  );
                  setIsCalendlyOpen(true);
                }}
                variant="ghost-light"
                size="lg"
                className="border-gold text-gold hover:bg-gold hover:text-navy rounded-full shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 event-consultation-track"
                data-oid="9dl3rhc"
              >
                {t("events.discussCorporatePackages")}
              </Button>
            </div>
          </div>
        </section>

        {/* Private Parties Section - Full Screen - HIDDEN (now in hero) */}
        <section
          className="hidden relative h-screen flex items-center justify-center overflow-hidden z-30"
          data-oid="qkbf44k"
        >
          <LazyBackgroundImage
            src={privatePartiesBg}
            alt="Private party celebration at Beatenberg Resort"
            className="absolute inset-0"
            rootMargin="100px"
            data-oid="nc:shu2"
          />

          <div
            className="relative z-10 max-w-7xl mx-auto px-6 w-full"
            data-oid="uof-804"
          >
            <div className="text-center mb-16 relative" data-oid="w:e7unf">
              {/* Smooth shadow overlay behind text - oval shape to fit text layout */}
              <div
                className="absolute inset-0 flex items-center justify-center"
                data-oid="g4cboki"
              >
                <div
                  className="w-[900px] h-[400px] rounded-full opacity-60"
                  style={{
                    background:
                      "radial-gradient(ellipse, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.4) 50%, transparent 70%)",
                  }}
                  data-oid="9qiitd0"
                />
              </div>
              <div className="relative z-10" data-oid="jx:9kos">
                <div className="flex justify-center mb-6" data-oid="n3d54.5">
                  <PartyPopper
                    className="text-gold text-8xl"
                    style={{
                      filter: "drop-shadow(2px 2px 8px rgba(0,0,0,0.8))",
                    }}
                    data-oid="otm5zt7"
                  />
                </div>
                <h2
                  className="text-5xl md:text-6xl font-playfair mb-6 text-white"
                  style={{
                    filter: "drop-shadow(2px 2px 8px rgba(0,0,0,0.8))",
                  }}
                  data-oid="8kh2x0w"
                >
                  {t("events.privatePartiesTitle")}
                </h2>
                <p
                  className="text-xl text-white/95 max-w-3xl mx-auto"
                  style={{
                    filter: "drop-shadow(1px 1px 4px rgba(0,0,0,0.8))",
                  }}
                  data-oid="2wt9afc"
                >
                  {t("events.privatePartiesDescription")}
                </p>
              </div>
            </div>

            <div
              className="flex flex-wrap gap-4 justify-center items-center w-full relative"
              data-oid="3u.picx"
            >
              {/* Smooth shadow overlay for feature items - oval shape */}
              <div
                className="absolute inset-0 flex items-center justify-center"
                data-oid="p4n9c_i"
              >
                <div
                  className="w-[700px] h-[150px] rounded-full opacity-60"
                  style={{
                    background:
                      "radial-gradient(ellipse, rgba(0,0,0,0.5) 0%, rgba(0,0,0,0.2) 50%, transparent 70%)",
                  }}
                  data-oid="-ts4y1l"
                />
              </div>
              <div
                className="flex flex-col items-center text-center relative z-10"
                data-oid="vl-.w3n"
              >
                <Gift
                  className="text-white mb-2"
                  size={24}
                  style={{ filter: "drop-shadow(1px 1px 4px rgba(0,0,0,0.8))" }}
                  data-oid="ugfdond"
                />

                <span
                  className="text-white text-sm md:text-base font-medium uppercase tracking-wider"
                  style={{ filter: "drop-shadow(1px 1px 4px rgba(0,0,0,0.8))" }}
                  data-oid="1qa38m1"
                >
                  {t("events.birthdayCelebrations")}
                </span>
              </div>
              <div
                className="w-[1.5px] h-5 bg-white/50 relative z-10 hidden md:block"
                style={{ filter: "drop-shadow(1px 1px 2px rgba(0,0,0,0.5))" }}
                data-oid="_3ip48r"
              ></div>
              <div
                className="flex flex-col items-center text-center relative z-10"
                data-oid="pv.z9ii"
              >
                <Heart
                  className="text-white mb-2"
                  size={24}
                  style={{ filter: "drop-shadow(1px 1px 4px rgba(0,0,0,0.8))" }}
                  data-oid="8dmz3.i"
                />

                <span
                  className="text-white text-sm md:text-base font-medium uppercase tracking-wider"
                  style={{ filter: "drop-shadow(1px 1px 4px rgba(0,0,0,0.8))" }}
                  data-oid="y3fvs1v"
                >
                  {t("events.anniversaries")}
                </span>
              </div>
              <div
                className="w-[1.5px] h-5 bg-white/50 relative z-10 hidden md:block"
                style={{ filter: "drop-shadow(1px 1px 2px rgba(0,0,0,0.5))" }}
                data-oid="ystl-bn"
              ></div>
              <div
                className="flex flex-col items-center text-center relative z-10"
                data-oid="fy6pxxn"
              >
                <Crown
                  className="text-white mb-2"
                  size={24}
                  style={{ filter: "drop-shadow(1px 1px 4px rgba(0,0,0,0.8))" }}
                  data-oid="5wtkw.9"
                />

                <span
                  className="text-white text-sm md:text-base font-medium uppercase tracking-wider"
                  style={{ filter: "drop-shadow(1px 1px 4px rgba(0,0,0,0.8))" }}
                  data-oid="sr8mopa"
                >
                  {t("events.luxuryGalas")}
                </span>
              </div>
            </div>
            <div className="text-center mt-12" data-oid="4c00b.0">
              <Button
                onClick={() => {
                  trackEventConsultation(
                    "private_party",
                    t("events.discussPrivatePartyPackages"),
                    "private_party_section",
                    "calendly",
                  );
                  setIsCalendlyOpen(true);
                }}
                variant="ghost-light"
                size="lg"
                className="border-gold text-gold hover:bg-gold hover:text-navy rounded-full shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 event-consultation-track"
                data-oid="f2v83y-"
              >
                {t("events.discussPrivatePartyPackages")}
              </Button>
            </div>
          </div>
        </section>

        {/* Complete Event Experiences Section - Full Screen with Fixed Background */}
        <section
          className="relative py-12 overflow-hidden bg-white"
          data-oid="tinhjvq"
        >

          <div
            className="relative z-20 max-w-7xl mx-auto px-4 sm:px-6 w-full"
            data-oid="jn4bu5i"
          >
            <div className="text-center mb-6 sm:mb-8" data-oid="g2qzp5i">
              <h2
                className="text-3xl sm:text-4xl md:text-5xl font-playfair mb-3 sm:mb-4 text-foreground"
                data-oid="7uvp718"
              >
                {t("events.completeEventExperiences")}
              </h2>
              <p
                className="text-base sm:text-lg text-muted-foreground max-w-3xl mx-auto mb-2 px-2"
                data-oid="iks389z"
              >
                {t("events.completeEventExperiencesDescription")}
              </p>
            </div>

            {/* Tab Navigation */}
            <div
              className="flex flex-col sm:flex-row justify-center mb-8 gap-3 sm:gap-2"
              data-oid="rhybigc"
            >
              {services.map((service, index) => (
                <button
                  key={index}
                  onClick={() => setActiveTab(index)}
                  className={`flex items-center justify-center px-4 sm:px-6 md:px-8 py-3 sm:py-4 rounded-lg transition-all duration-300 w-full sm:w-auto ${
                    activeTab === index
                      ? "bg-gold text-navy shadow-lg transform scale-105"
                      : "bg-white text-foreground hover:bg-gray-100 hover:scale-102 border border-gray-200"
                  }`}
                  data-oid="r94-4nm"
                >
                  <service.icon
                    size={20}
                    className="mr-2 sm:mr-3"
                    data-oid="lubev9a"
                  />

                  <span
                    className="font-medium text-sm sm:text-base md:text-lg"
                    data-oid="5ejlxzu"
                  >
                    {service.title}
                  </span>
                </button>
              ))}
            </div>

            {/* Tab Content */}
            <div className="max-w-5xl mx-auto mb-8" data-oid="lu8ktd-">
              <Card
                className="relative bg-white border-gray-200 text-foreground p-4 sm:p-6 md:pt-10 md:px-10 md:pb-6 w-full min-h-[400px] sm:min-h-[500px] flex flex-col shadow-lg"
                data-oid="jt.vqpl"
              >
                <div className="relative z-10 flex flex-col" data-oid="xpvv:vh">
                  <div
                    className="flex flex-col sm:flex-row items-start sm:items-center mb-6 sm:mb-8"
                    data-oid="sv35zo7"
                  >
                    <div
                      className="text-gold mr-0 sm:mr-6 mb-4 sm:mb-0"
                      data-oid="vjygj47"
                    >
                      {React.createElement(services[activeTab].icon, {
                        size: 40,
                      })}
                    </div>
                    <div data-oid="hdj54uj">
                      <h3
                        className="text-xl sm:text-2xl md:text-3xl font-playfair mb-3 sm:mb-4"
                        data-oid="rj-9n.g"
                      >
                        {services[activeTab].title}
                      </h3>
                      <p
                        className="text-base sm:text-lg text-muted-foreground leading-relaxed"
                        data-oid="v-zj9m0"
                      >
                        {services[activeTab].description}
                      </p>
                    </div>
                  </div>

                  <div
                    className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 mb-6 sm:mb-8"
                    data-oid="ayolefe"
                  >
                    {/* Service Details */}
                    <div data-oid="wb03xz.">
                      <h4
                        className="text-lg sm:text-xl font-playfair mb-3 sm:mb-4 text-gold font-semibold"
                        data-oid="0f7i5vs"
                      >
                        {t("events.serviceOverview")}
                      </h4>
                      <p
                        className="text-muted-foreground leading-relaxed mb-4 sm:mb-6 text-sm sm:text-base"
                        data-oid=".usufoa"
                      >
                        {services[activeTab].details}
                      </p>

                      <h4
                        className="text-base sm:text-lg font-playfair mb-3 sm:mb-4 text-gold font-semibold"
                        data-oid="kbsmr_l"
                      >
                        {t("events.keyFeatures")}
                      </h4>
                      <ul className="space-y-2 sm:space-y-3" data-oid="jnh8d.:">
                        {services[activeTab].features.map((feature, idx) => (
                          <li
                            key={idx}
                            className="flex items-center text-foreground text-sm sm:text-base"
                            data-oid="ks-kz.."
                          >
                            <Star
                              size={12}
                              className="text-gold mr-2 sm:mr-3 flex-shrink-0"
                              data-oid="16n.ce7"
                            />

                            {feature}
                          </li>
                        ))}
                      </ul>
                    </div>

                    {/* Service Highlights */}
                    <div data-oid="c67kz27">
                      <h4
                        className="text-lg sm:text-xl font-playfair mb-3 sm:mb-4 text-gold font-semibold"
                        data-oid=":-zsmn5"
                      >
                        {t("events.whatWeProvide")}
                      </h4>
                      <ul
                        className="space-y-2 sm:space-y-3 mb-4 sm:mb-6"
                        data-oid="c5.u9j9"
                      >
                        {services[activeTab].highlights.map(
                          (highlight, idx) => (
                            <li
                              key={idx}
                              className="flex items-start text-foreground text-sm sm:text-base"
                              data-oid="rq93:tw"
                            >
                              <div
                                className="w-2 h-2 bg-gold rounded-full mt-2 mr-2 sm:mr-3 flex-shrink-0"
                                data-oid="udr5waa"
                              ></div>
                              <span data-oid="17dzzwr">{highlight}</span>
                            </li>
                          ),
                        )}
                      </ul>
                      <Button
                        onClick={() => {
                          // Map service titles to event types
                          const eventTypeMap: Record<
                            number,
                            "wedding" | "corporate" | "private_party"
                          > = {
                            0: "wedding",
                            1: "corporate",
                            2: "private_party",
                          };
                          const serviceCategories = [
                            "wedding_planning",
                            "corporate_events",
                            "private_celebrations",
                          ];

                          trackEventConsultation(
                            eventTypeMap[activeTab],
                            `${t("events.getStartedWith")} ${services[activeTab].title}`,
                            "event_experiences_section",
                            "calendly",
                            serviceCategories[activeTab],
                          );
                          setIsCalendlyOpen(true);
                        }}
                        variant="luxury"
                        size="lg"
                        className="text-sm sm:text-lg px-6 sm:px-8 py-3 sm:py-4 w-full sm:w-auto rounded-full shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 event-consultation-track"
                        data-oid="ecxaz.u"
                      >
                        {t("events.getStartedWith")} {services[activeTab].title}
                      </Button>
                    </div>
                  </div>
                </div>
              </Card>
            </div>

            {/* Premium Features Grid */}
            <div className="mb-12" data-oid="es3phnq">
              <h3
                className="text-2xl sm:text-3xl font-playfair text-foreground text-center mb-6 sm:mb-8"
                data-oid="pw6v9.0"
              >
                {t("events.premiumEventFeatures")}
              </h3>
              <div
                className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6"
                data-oid="yvg65t_"
              >
                <div
                  className="bg-white rounded-lg p-4 sm:p-6 text-center border border-gray-200 shadow-sm"
                  data-oid="yettitg"
                >
                  <div
                    className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-gold to-yellow-600 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4"
                    data-oid="mbsl2uo"
                  >
                    <Calendar
                      className="w-5 h-5 sm:w-6 sm:h-6 text-white"
                      data-oid="vmsfffh"
                    />
                  </div>
                  <h4
                    className="text-base sm:text-lg font-playfair text-foreground mb-2"
                    data-oid="k3iijoq"
                  >
                    {t("events.fullServiceEventPlanning")}
                  </h4>
                  <p
                    className="text-muted-foreground text-xs sm:text-sm"
                    data-oid="7g2vrm2"
                  >
                    {t("events.fullServiceEventPlanningDesc")}
                  </p>
                </div>

                <div
                  className="bg-white rounded-lg p-4 sm:p-6 text-center border border-gray-200 shadow-sm"
                  data-oid="7h9anac"
                >
                  <div
                    className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-gold to-yellow-600 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4"
                    data-oid="wpx_lq."
                  >
                    <UtensilsCrossed
                      className="w-5 h-5 sm:w-6 sm:h-6 text-white"
                      data-oid="lsyp04l"
                    />
                  </div>
                  <h4
                    className="text-base sm:text-lg font-playfair text-foreground mb-2"
                    data-oid="wcs-xwl"
                  >
                    {t("events.gourmetCateringServices")}
                  </h4>
                  <p
                    className="text-muted-foreground text-xs sm:text-sm"
                    data-oid="scbh_p1"
                  >
                    {t("events.gourmetCateringServicesDesc")}
                  </p>
                </div>

                <div
                  className="bg-white rounded-lg p-4 sm:p-6 text-center border border-gray-200 shadow-sm"
                  data-oid="d:zv48d"
                >
                  <div
                    className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-gold to-yellow-600 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4"
                    data-oid="4imcq1b"
                  >
                    <Wifi
                      className="w-5 h-5 sm:w-6 sm:h-6 text-white"
                      data-oid="kc5x7qq"
                    />
                  </div>
                  <h4
                    className="text-base sm:text-lg font-playfair text-foreground mb-2"
                    data-oid="xn.u714"
                  >
                    {t("events.stateOfTheArtTechnology")}
                  </h4>
                  <p
                    className="text-muted-foreground text-xs sm:text-sm"
                    data-oid="og-veb8"
                  >
                    {t("events.stateOfTheArtTechnologyDesc")}
                  </p>
                </div>

                <div
                  className="bg-white rounded-lg p-4 sm:p-6 text-center border border-gray-200 shadow-sm"
                  data-oid="q8-:nqh"
                >
                  <div
                    className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-gold to-yellow-600 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4"
                    data-oid="7mgu:m9"
                  >
                    <Mountain
                      className="w-5 h-5 sm:w-6 sm:h-6 text-white"
                      data-oid="_::u2mb"
                    />
                  </div>
                  <h4
                    className="text-base sm:text-lg font-playfair text-foreground mb-2"
                    data-oid="nqxip2g"
                  >
                    {t("events.wellnessIntegration")}
                  </h4>
                  <p
                    className="text-muted-foreground text-xs sm:text-sm"
                    data-oid="hbt6..g"
                  >
                    {t("events.wellnessIntegrationDesc")}
                  </p>
                </div>

                <div
                  className="bg-white rounded-lg p-4 sm:p-6 text-center border border-gray-200 shadow-sm"
                  data-oid="9u:2b-:"
                >
                  <div
                    className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-gold to-yellow-600 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4"
                    data-oid="n_67tqz"
                  >
                    <Camera
                      className="w-5 h-5 sm:w-6 sm:h-6 text-white"
                      data-oid="t3tnid7"
                    />
                  </div>
                  <h4
                    className="text-base sm:text-lg font-playfair text-foreground mb-2"
                    data-oid="mg_b1i3"
                  >
                    {t("events.digitalLedWallFeature")}
                  </h4>
                  <p
                    className="text-muted-foreground text-xs sm:text-sm"
                    data-oid="3lvcn4j"
                  >
                    {t("events.digitalLedWallFeatureDesc")}
                  </p>
                </div>

                <div
                  className="bg-white rounded-lg p-4 sm:p-6 text-center border border-gray-200 shadow-sm"
                  data-oid="b09fh76"
                >
                  <div
                    className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-gold to-yellow-600 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4"
                    data-oid="nup7fk7"
                  >
                    <Music
                      className="w-5 h-5 sm:w-6 sm:h-6 text-white"
                      data-oid="qgj4yzu"
                    />
                  </div>
                  <h4
                    className="text-base sm:text-lg font-playfair text-foreground mb-2"
                    data-oid="rozvfbh"
                  >
                    {t("events.audioSystemsFeature")}
                  </h4>
                  <p
                    className="text-muted-foreground text-xs sm:text-sm"
                    data-oid="2vyfl4."
                  >
                    {t("events.audioSystemsFeatureDesc")}
                  </p>
                </div>

                <div
                  className="bg-white rounded-lg p-4 sm:p-6 text-center border border-gray-200 shadow-sm"
                  data-oid="7h75h-s"
                >
                  <div
                    className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-gold to-yellow-600 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4"
                    data-oid="u4wn:yo"
                  >
                    <Sparkles
                      className="w-5 h-5 sm:w-6 sm:h-6 text-white"
                      data-oid="jde3i53"
                    />
                  </div>
                  <h4
                    className="text-base sm:text-lg font-playfair text-foreground mb-2"
                    data-oid="c48.s1b"
                  >
                    {t("events.eventPlanningFeature")}
                  </h4>
                  <p
                    className="text-muted-foreground text-xs sm:text-sm"
                    data-oid="qj74p3z"
                  >
                    {t("events.eventPlanningFeatureDesc")}
                  </p>
                </div>

                <div
                  className="bg-white rounded-lg p-4 sm:p-6 text-center border border-gray-200 shadow-sm"
                  data-oid="gsu1q0m"
                >
                  <div
                    className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-gold to-yellow-600 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4"
                    data-oid="0dx4vtk"
                  >
                    <Star
                      className="w-5 h-5 sm:w-6 sm:h-6 text-white"
                      data-oid=":p3obal"
                    />
                  </div>
                  <h4
                    className="text-base sm:text-lg font-playfair text-foreground mb-2"
                    data-oid="cs2qc7."
                  >
                    {t("events.premiumAmenities")}
                  </h4>
                  <p
                    className="text-muted-foreground text-xs sm:text-sm"
                    data-oid="vieweu."
                  >
                    {t("events.premiumAmenitiesDesc")}
                  </p>
                </div>
              </div>
            </div>

            {/* Parking Information */}
            <div className="text-center" data-oid="nqs.h.v">
              <div
                className="bg-white rounded-lg p-6 sm:p-8 border border-gray-200 shadow-sm max-w-2xl mx-auto"
                data-oid="11z9ef8"
              >
                <div
                  className="flex items-center justify-center gap-2 sm:gap-3 mb-3 sm:mb-4"
                  data-oid="lss1l.e"
                >
                  <MapPin
                    className="w-6 h-6 sm:w-8 sm:h-8 text-gold"
                    data-oid="b9x.rdo"
                  />

                  <h4
                    className="text-xl sm:text-2xl font-playfair text-foreground"
                    data-oid="a8px-xh"
                  >
                    {t("events.convenientParkingTitle")}
                  </h4>
                </div>
                <p
                  className="text-muted-foreground text-base sm:text-lg mb-2"
                  data-oid="l13p1qf"
                >
                  {t("events.convenientParkingCapacity")}
                </p>
                <p
                  className="text-muted-foreground text-xs sm:text-sm"
                  data-oid="szhm883"
                >
                  {t("events.complimentaryParking")}
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Portfolio Section - Events Gallery */}
        <AutoScrollGallery
          id="events-gallery"
          eyebrow={t("events.ourPortfolio")}
          title={t("events.eventGallery")}
          subtitle={t("events.eventGallerySubtitle")}
          images={[
            {
              src: "/images/Events/Dream wedding 1.webp",
              alt: "Dream Wedding Celebration",
            },
            {
              src: "/images/Events/Dream Wedding.webp",
              alt: "Luxury Wedding Ceremony",
            },
            {
              src: "/images/Events/indian wedding.webp",
              alt: "Traditional Indian Wedding",
            },
            {
              src: "/images/Events/Private parties.webp",
              alt: "Private Party Celebration",
            },
            {
              src: "/images/Events/private party 1.webp",
              alt: "Intimate Celebration",
            },
            {
              src: "/images/Events/private party 2.webp",
              alt: "Exclusive Event",
            },
            {
              src: "/images/Events/private parties 4.webp",
              alt: "Premium Party Experience",
            },
            {
              src: "/images/Events/private parties 5.webp",
              alt: "Luxury Celebration",
            },
            {
              src: "/images/Events/private parties 6.webp",
              alt: "Sophisticated Gathering",
            },
            {
              src: "/images/Events/meeting 2.webp",
              alt: "Executive Conference",
            },
            {
              src: "/images/Events/presentation.webp",
              alt: "Business Presentation",
            },
          ]}
          showOverlayButton={false}
          scrollSpeed={40}
          imageHeight="h-[500px]"
          data-oid="0b0u1ya"
        />

        {/* Seamless Background Transition */}
        <div
          className="h-6 md:h-16 bg-gradient-to-b from-white to-white pointer-events-none"
          data-oid="3e_0wtf"
        ></div>

        {/* Testimonials Section */}
        <section
          className="py-20 px-6 bg-white relative z-40"
          data-oid="_:q.m65"
        >
          <div className="max-w-7xl mx-auto" data-oid="onw_q7.">
            <div className="text-center mb-16" data-oid="q8eay1-">
              <h2
                className="text-4xl md:text-5xl font-playfair mb-6 text-foreground"
                data-oid="wxk:qcr"
              >
                {t("events.whatOurClientsSay")}
              </h2>
              <p className="text-xl text-muted-foreground" data-oid="elrsayr">
                {t("events.whatOurClientsSayDescription")}
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8" data-oid="yxk3b24">
              {testimonials.map((testimonial, index) => (
                <Card
                  key={index}
                  className="p-8 text-center border-border/50"
                  data-oid="rij:r4x"
                >
                  <div className="flex justify-center mb-4" data-oid="f6gqrk9">
                    {[...Array(5)].map((_, i) => (
                      <Star
                        key={i}
                        size={20}
                        className="text-gold fill-current"
                        data-oid="q2aftph"
                      />
                    ))}
                  </div>
                  <blockquote
                    className="text-muted-foreground mb-6 italic leading-relaxed"
                    data-oid="a-2nf:2"
                  >
                    "{testimonial.text}"
                  </blockquote>
                  <div data-oid="99856q1">
                    <p
                      className="font-semibold text-foreground"
                      data-oid="292ja7j"
                    >
                      {testimonial.name}
                    </p>
                    <p
                      className="text-sm text-muted-foreground"
                      data-oid="czfzrqs"
                    >
                      {testimonial.event}
                    </p>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section with inline Calendly embed */}
        <section
          className="py-12 md:py-16 bg-gradient-sunset relative z-40"
          data-oid="j6x5mb2"
        >
          {/* Darken gradient for readability */}
          <div
            className="absolute inset-0 bg-black/20 md:bg-black/25 pointer-events-none"
            data-oid="ji:ps0:"
          ></div>
          <div
            className="max-w-7xl mx-auto px-6 relative z-10"
            data-oid="r:3:02r"
          >
            <div
              className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8 items-center"
              data-oid="_m6pn5e"
            >
              {/* Left: CTA copy and buttons */}
              <div
                className="text-center md:text-left flex flex-col justify-center"
                data-oid="eg7i_7:"
              >
                <h2
                  className="text-3xl md:text-4xl font-playfair mb-4 text-white"
                  data-oid="0ob8tor"
                >
                  {t("events.readyToCreate")}
                </h2>
                <p
                  className="text-lg md:text-xl text-white/90 mb-6 leading-relaxed"
                  data-oid="km-r0ax"
                >
                  {t("events.readyToCreateDescription")}
                </p>
              </div>

              {/* Right: Calendly embed */}
              <div
                className="bg-white rounded-xl border border-border/50 shadow-sm overflow-hidden"
                style={{ 
                  height: '600px', 
                  maxHeight: '600px',
                  position: 'relative'
                }}
                data-oid="tci.da8"
              >
                <CalendlyEmbed
                  height={600}
                  eventType="general"
                  location="events_cta_section_embed"
                  data-oid="dbmzm0s"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Removed separate Calendly section; Calendly is now embedded in CTA */}

        {/* Calendly Modal */}
        <CalendlyModal
          isOpen={isCalendlyOpen}
          onClose={() => setIsCalendlyOpen(false)}
          eventType="general"
          location="events_calendly_modal"
          data-oid="q-rrygg"
        />

        {/* Contact Info */}
        <section
          className="py-10 bg-navy text-white relative z-40"
          data-oid="sijg:z-"
        >
          <div className="max-w-7xl mx-auto px-6" data-oid=":_.ihaq">
            <div
              className="grid md:grid-cols-4 gap-8 text-center"
              data-oid="rywokvg"
            >
              <div data-oid="rbt58uo">
                <Calendar
                  className="mx-auto mb-4 text-gold"
                  size={32}
                  data-oid="kdsavl9"
                />

                <h3 className="font-semibold mb-2" data-oid="ncblquy">
                  {t("events.planningTimeline")}
                </h3>
                <p className="text-white/80" data-oid="h3f.xf1">
                  {t("events.planningTimelineDesc")}
                </p>
              </div>
              <div data-oid="45e:xw1">
                <Users
                  className="mx-auto mb-4 text-gold"
                  size={32}
                  data-oid="0ly.li7"
                />

                <h3 className="font-semibold mb-2" data-oid="6rrv0ss">
                  {t("events.eventCapacity")}
                </h3>
                <p className="text-white/80" data-oid="y5l0:.c">
                  {t("events.eventCapacityDesc")}
                </p>
              </div>
              <div data-oid="6f9_:1c">
                <Camera
                  className="mx-auto mb-4 text-gold"
                  size={32}
                  data-oid="4-i3we1"
                />

                <h3 className="font-semibold mb-2" data-oid="4ijjf8n">
                  {t("events.documentation")}
                </h3>
                <p className="text-white/80" data-oid="bc6iekq">
                  {t("events.documentationDesc")}
                </p>
              </div>
              <div data-oid="e-ryjpd">
                <Music
                  className="mx-auto mb-4 text-gold"
                  size={32}
                  data-oid="ll.9:o8"
                />

                <h3 className="font-semibold mb-2" data-oid="ydj:fo4">
                  {t("events.entertainmentFeature")}
                </h3>
                <p className="text-white/80" data-oid="790czbd">
                  {t("events.entertainmentFeatureDesc")}
                </p>
              </div>
            </div>
          </div>
        </section>

        <Footer data-oid=".ka-.20" />

        {/* Contact Concierge Modal */}
        <ContactConciergeModal
          isOpen={isContactModalOpen}
          onClose={() => setIsContactModalOpen(false)}
          data-oid=":togtmw"
        />

        {/* Event Contact Modal */}
        <EventContactModal
          isOpen={isEventContactModalOpen}
          onClose={() => setIsEventContactModalOpen(false)}
          data-oid="jna2sz_"
        />
      </div>
    </>
  );
};

export default Events;
